import { Component, ViewChild, ElementRef, OnInit, AfterViewInit, QueryList, ViewChildren, signal, NO_ERRORS_SCHEMA } from '@angular/core';
import { FlowDefinition, FlowDefinitionContext } from '../../models/flow.model';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { BaseComponent } from '../../components/base/base.component';
import { Enums } from '../../shared/enums';
import { AuthService } from '../../services/auth.service';
import { SCSS_VARS } from '../../shared/shared-scss-ts-variables';
import { FlowDefinitionItemComponent } from '../../components/iterables/flow-definition-item/flow-definition-item.component';
import { FlowDefinitionContextService } from '../../services/flow-definition-context.service';
import { Utils } from '../../shared/utils';
import { IMPORTS } from './_imports';
import { Unidade } from '../../models/organograma.model';
import { AcessoCidadaoService } from '../../services/acesso-cidadao.service';
import { Location } from '@angular/common';
import { FlowInboxDirectory } from 'src/app/models/flow-inbox-directory.model';
import { FlowInboxDirectoryService } from 'src/app/services/flow-inbox-directory.service';
import { IsActiveFilter, IsPublishedFilter, NameFilter } from '../flow-definition-list/flow-definition-list.pipe';

@Component({
    selector: 'flow-inbox-directory',
    templateUrl: './flow-inbox-directory.component.html',
    styleUrl: './flow-inbox-directory.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowInboxDirectoryComponent extends BaseComponent implements OnInit, AfterViewInit {
    // #region [ViewChild]
    @ViewChild('inputFilterRef') inputFilterRef: ElementRef<HTMLInputElement>;
    @ViewChild('removeModeButtonRef') removeModeButtonRef: ElementRef;
    @ViewChildren('contextRef') contextRef: QueryList<ElementRef>;
    @ViewChildren('flowItemRef') flowItemRef: QueryList<FlowDefinitionItemComponent>;
    // #endregion

    // #region [const]
    selectedFlowInboxConfig: any = {
        search: true,
        searchOnKey: 'description',
        displayKey: 'description',
        placeholder: 'Buscar',
        searchPlaceholder: 'Filtrar',
        moreText: 'Mais',
        noResultsFound: 'Nenhum resultado encontrado',
        height: '500px'
    } as const;
    // #endregion

    // #region [props]
    adminUnitsList: Unidade[] = [];
    // #endregion

    // #region [signals]
    $flowDefinitionContexts = signal([] as FlowDefinitionContext[]);
    $flowDefinitions = signal(null as FlowDefinition[]);
    $filteredFlowDefinitions = signal([] as FlowDefinition[]);
    $selectedFlowInboxObject = signal(null as any);
    $currentDirectory = signal(null as FlowInboxDirectory)
    $isBootstrapFinished = signal(false);
    $isFlowItemVisible = signal(false);
    $isActiveFilterOn = signal(false);
    $isActiveFilterOff = signal(false);
    $isPublishedFilterOn = signal(false);
    $isPublishedFilterOff = signal(false);
    $isRemovingFromDirectory = signal(false);
    // #endregion

    constructor(
        route: ActivatedRoute,
        router: Router,
        spinner: NgxSpinnerService,
        toastr: ToastrService,
        private nameFilter: NameFilter,
        private isActiveFilter: IsActiveFilter,
        private isPublishedFilter: IsPublishedFilter,
        private flowDefinitionContextService: FlowDefinitionContextService,
        private acessoCidadaoService: AcessoCidadaoService,
        private authService: AuthService,
        private location: Location,
        private flowInboxDirectoryService: FlowInboxDirectoryService,
    ) {
        super(route, router, spinner, toastr);
    }

    async ngOnInit(skipLoading?: boolean, flowInboxToLoad?: string, isFromCanvasList?: boolean) {
        if (!skipLoading) {
            this.spinner.show();
        }

        if (isFromCanvasList) {
            setTimeout(() => {
                this.$isRemovingFromDirectory.set(false);
                this.removeModeButtonRef.nativeElement.hidden = true;
            }, 200);
            setTimeout(() => {
                this.$isRemovingFromDirectory.set(true);
                this.removeModeButtonRef.nativeElement.hidden = false;
            }, 500);
        }

        if (flowInboxToLoad != null && this.paramId != null) {
            this.location.replaceState(Enums.PagesPaths.FlowInboxDirectory);
        } else if (this.paramId != null) {
            flowInboxToLoad = this.paramId;
        }

        const response = await this.acessoCidadaoService.getUnidadesPerfilAdministrador(this.authService.$user().id);
        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }
        this.adminUnitsList = response.data;

        this.getLocalStorage();

        try {
            const directoryId = this.route.snapshot.paramMap.get('id');

            if (directoryId) {
                const response = await this.flowInboxDirectoryService.getById(directoryId);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.$currentDirectory.set(response.data);
                this.$selectedFlowInboxObject.set(this.adminUnitsList.find(x => x.guid == this.$currentDirectory().flowInboxId));
                this.initializeCollections(response.data.flowDefinitions);
            }

        } finally {
            setTimeout(() => {
                this.$isBootstrapFinished.set(true);
                this.spinner.hide();
                setTimeout(() => this.$isFlowItemVisible.set(true), 400);
            }, 400);
        }
    }

    ngAfterViewInit() {
        let interval = setInterval(() => {
            if (this.$isBootstrapFinished()) {
                this.adjustContextCards();
                clearInterval(interval);
            }
        }, 100);
    }

    toggleIsActive(isOnOnly: boolean) {
        if (isOnOnly == null) {
            this.$isActiveFilterOn.set(false);
            this.$isActiveFilterOff.set(false);
        } else if (isOnOnly) {
            this.$isActiveFilterOn.set(true);
            this.$isActiveFilterOff.set(false);
        } else {
            this.$isActiveFilterOn.set(false);
            this.$isActiveFilterOff.set(true);
        }

        this.setLocalStorage();
        this.runFilter();
    }

    toggleIsPublished(isOnOnly: boolean) {
        if (isOnOnly == null) {
            this.$isPublishedFilterOn.set(false);
            this.$isPublishedFilterOff.set(false);
        } else if (isOnOnly) {
            this.$isPublishedFilterOn.set(true);
            this.$isPublishedFilterOff.set(false);
        } else {
            this.$isPublishedFilterOn.set(false);
            this.$isPublishedFilterOff.set(true);
        }

        this.setLocalStorage();
        this.runFilter();
    }

    clearFilter() {
        this.inputFilterRef.nativeElement.value = '';
        this.runFilter();
    }

    runFilter() {
        this.$filteredFlowDefinitions.set(this.isPublishedFilter.transform(
            this.isActiveFilter.transform(
                this.nameFilter.transform(
                    this.$flowDefinitions(),
                    this.inputFilterRef?.nativeElement.value
                ),
                this.$isActiveFilterOn(),
                this.$isActiveFilterOff()
            ),
            this.$isPublishedFilterOn(),
            this.$isPublishedFilterOff()
        ));

        this.adjustContextCards();
    }

    async changeFlowInbox(event) {
        Utils.setSignal(this.$selectedFlowInboxObject, event);

        this.setLocalStorage();
        await this.ngOnInit(false, this.$selectedFlowInboxObject().guid);
    }

    openFlowInbox() {
        setTimeout(() => {
            (document.querySelector('ngx-select-dropdown .search-container > input') as HTMLElement).focus();
        }, 1);
    }

    getOrderedAdminUnitsList(): any[] {
        return this.adminUnitsList.sort((a, b) => {
            let first = `${a.organizacao.organizacaoPai?.sigla || ''}${a.organizacao.sigla} - ${a.nomeCurto}`;
            let second = `${b.organizacao.organizacaoPai?.sigla || ''}${b.organizacao.sigla} - ${b.nomeCurto}`;
            return first.localeCompare(second);
        }).map(x => Object.assign(x, {
            description: `${x.organizacao.organizacaoPai?.sigla != null ? x.organizacao.organizacaoPai.sigla + ' - ' : ''}${x.organizacao.sigla} - ${x.nomeCurto}`
        }));
    }

    exitDirectory() {
        this.router.navigate([Enums.PagesPaths.FlowDefinitionList]);
    }

    toggleRemoveMode() {
        this.$isRemovingFromDirectory.update(state => !state);
    }

    // #region [===== private methods =====]
    // #endregion

    private initializeCollections(responseData: FlowDefinition[]) {
        Utils.setSignal(this.$flowDefinitions, responseData);
        this.$flowDefinitionContexts.set([]);

        this.$flowDefinitions().forEach(item => {
            if (item.contextId == null) {
                Utils.updSignal(this.$flowDefinitionContexts, x => {
                    x.push({ flowDefinitions: [item] });
                    return x;
                });
            } else {
                let context = this.$flowDefinitionContexts().find(x => x.id == item.contextId);
                if (context == null) {
                    Utils.updSignal(this.$flowDefinitionContexts, x => {
                        x.push({ id: item.contextId, flowDefinitions: [item] });
                        return x;
                    });
                } else {
                    context.flowDefinitions.push(item);
                }
            }
        });

        Utils.updSignal(this.$flowDefinitionContexts, x => {
            x.forEach(async item => {
                item.flowDefinitions.sort((a, b) => a.contextIndex - b.contextIndex);

                if (item.id != null) {
                    const response = await this.flowDefinitionContextService.getById(item.id);

                    if (!response.isSuccess) {
                        this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                        return;
                    }

                    item.name = response.data.name;
                }
            });
            return x;
        });

        this.runFilter();
    }

    // resolve o layout dos flow-definition-items quando pertencentes a um Context
    private adjustContextCards() {
        setTimeout(() => {
            let contexts = this.contextRef.toArray();
            if (contexts == null) return;

            contexts.forEach(item => {
                let context = item.nativeElement;
                let flowItems = this.flowItemRef.toArray().filter(x => [...context.children].includes(x.element.nativeElement));
                const flowItemOffset = Number.parseInt(SCSS_VARS['$context-card-top-offset'].replace('px', ''));

                if (flowItems.length > 1) {
                    let totalHeight = 0;
                    flowItems.forEach(flowItem => totalHeight += flowItem.element.nativeElement.offsetHeight);

                    const totalOffset = (flowItems.length - 1) * flowItemOffset;

                    // calcula a altura total do container (div) do Context
                    context.style.height = `${totalHeight + totalOffset}px`;
                }

                flowItems.forEach((item, idx) => {
                    // posiciona os flow-definition-items relativamente ao container (div) do Context
                    // flow-definition-items já estão ordenados pelo "contextIndex"; seguir o "idx" evita problemas com flow-definition-items filtrados
                    let contextIndex = idx;
                    let flowItemElement = item.element.nativeElement.children[0] as HTMLElement;
                    flowItemElement.style.top = contextIndex == null || contextIndex == 0 ? null : `${contextIndex * flowItemOffset}px`;

                    // aplica efeito de profundidade/offset aos flow-definition-items inferiores
                    let sideOffset = 5; /* px */
                    flowItemElement.style.width = `calc(100% - ${sideOffset * 2 * contextIndex}px)`;
                    flowItemElement.style.marginLeft = `${sideOffset * contextIndex}px`;
                });
            });
        }, 1);
    }

    private getLocalStorage() {
        // lê os valores do storage de filtros
        let filterStorage = window.localStorage.getItem(Enums.LocalStorageKeys.AdminFilter);
        let filterValues = [];
        if (filterStorage != null) {
            filterValues = atob(filterStorage).split(';');
        }

        // caso o storage exista
        if (filterValues.length >= 1) {
            // atribui os valores parseados do storage
            this.$isActiveFilterOn.set(!!JSON.parse(String(filterValues[0].split(':')[1]).toLowerCase()));
            this.$isActiveFilterOff.set(!!JSON.parse(String(filterValues[0].split(':')[2]).toLowerCase()));
            this.$isPublishedFilterOn.set(!!JSON.parse(String(filterValues[1].split(':')[1]).toLowerCase()));
            this.$isPublishedFilterOff.set(!!JSON.parse(String(filterValues[1].split(':')[2]).toLowerCase()));
        } else {
            // valores padrão dos filtros de "Ativos" e "Publicados"
            this.$isActiveFilterOn.set(true);
            this.$isActiveFilterOff.set(false);
            this.$isPublishedFilterOn.set(false);
            this.$isPublishedFilterOff.set(false);

            this.setLocalStorage();
        }

        // lê os valores do storage da Caixa de Fluxos
        let inboxStorage = window.localStorage.getItem(Enums.LocalStorageKeys.AdminFlowInbox);
        let inboxValue = '';
        if (inboxStorage != null) {
            inboxValue = atob(inboxStorage);
        }

        // caso o storage não exista
        if (inboxValue == '') {
            if (this.adminUnitsList.length > 0) {
                this.$selectedFlowInboxObject.set(this.getOrderedAdminUnitsList()[0]);

                this.setLocalStorage();
            } else {
                this.toastr.error(Enums.Messages.NoFlowInboxError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                console.error(Enums.Messages.NoFlowInboxError);
            }
        } else {
            let flowInbox = this.getOrderedAdminUnitsList().find(x => x.guid == inboxValue);
            if (flowInbox != null) {
                this.$selectedFlowInboxObject.set(this.getOrderedAdminUnitsList().find(x => x.guid == inboxValue));
            } else {
                this.$selectedFlowInboxObject.set(this.getOrderedAdminUnitsList()[0]);
            }
        }
    }

    private setLocalStorage() {
        // monta a string de valores no formato esperado
        let filterValues = `active:${this.$isActiveFilterOn()}:${this.$isActiveFilterOff()}`;
        filterValues += `;published:${this.$isPublishedFilterOn()}:${this.$isPublishedFilterOff()}`;

        // armazena os valores no storage de filtros
        window.localStorage.setItem(Enums.LocalStorageKeys.AdminFilter, btoa(filterValues));

        // armazena os valores no storage de Caixa de Fluxos
        window.localStorage.setItem(Enums.LocalStorageKeys.AdminFlowInbox, btoa(this.$selectedFlowInboxObject()?.guid || ""));
    }
}
